/**
 * ProcurementCatalogPane: virtualized tree view for the catalog (unselected) file pane.
 *
 * Renders project files that are NOT yet included in a procurement package as a
 * hierarchical folder/file tree. Uses @tanstack/react-virtual for efficient
 * rendering of large file sets, with folder expand/collapse, checkbox selection,
 * HTML5 drag-and-drop, and search filtering.
 */
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  Folder,
  FolderOpen,
  HelpCircle,
  Minus,
  Plus,
  Send,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import { getFileIcon } from "@/lib/fileIcons";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type {
  ProcurementPackageFileEvaluation,
  TitleBlockData,
} from "@/features/procurement/types";
import { useRubberBand } from "@/hooks/useRubberBand";

// ── Exported types ──

/** Minimal file metadata the tree needs from the parent. */
export interface ProcurementFileItem {
  fileId: string;
  fileName: string; // may contain "/" path separators for path hierarchy
  source?: string | null;
  isManual?: boolean;
  isIncluded?: boolean; // true when file is already in the package (greyed out in catalog)
  evaluation?: ProcurementPackageFileEvaluation;
  titleBlockData?: TitleBlockData | null;
}

// ── Internal tree types ──

interface TreeFolder {
  type: "folder";
  name: string;
  path: string;
  children: TreeEntry[];
}

interface TreeFile {
  type: "file";
  name: string;
  path: string;
  item: ProcurementFileItem;
}

type TreeEntry = TreeFolder | TreeFile;

/** A row in the flattened virtual list. */
type VirtualTreeRow =
  | {
      type: "folder";
      depth: number;
      name: string;
      path: string;
      fileCount: number;
      fileIds: string[];
      selectableFileIds: string[]; // non-included files only (for selection)
      isExpanded: boolean;
    }
  | {
      type: "file";
      depth: number;
      item: ProcurementFileItem;
    };

// ── Tree builder ──

function buildTree(items: ProcurementFileItem[]): TreeEntry[] {
  const roots: TreeEntry[] = [];
  const folderMap = new Map<string, TreeFolder>();

  // Preserve input order — caller sorts by folder then name with orphans at bottom
  for (const item of items) {
    const parts = item.fileName.split("/").filter(Boolean);
    if (parts.length === 0) continue;

    let currentChildren = roots;
    let currentPath = "";

    // Create ancestor folders
    for (let i = 0; i < parts.length - 1; i++) {
      currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
      if (!folderMap.has(currentPath)) {
        const folder: TreeFolder = {
          type: "folder",
          name: parts[i],
          path: currentPath,
          children: [],
        };
        folderMap.set(currentPath, folder);
        currentChildren.push(folder);
      }
      currentChildren = folderMap.get(currentPath)!.children;
    }

    // Add file leaf
    currentChildren.push({
      type: "file",
      name: parts[parts.length - 1],
      path: item.fileName,
      item,
    });
  }

  // Sort each level: folders first, then files
  const sortEntries = (entries: TreeEntry[]) => {
    entries.sort((a, b) => {
      if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    for (const e of entries) {
      if (e.type === "folder") sortEntries(e.children);
    }
  };
  sortEntries(roots);

  return roots;
}

/** Count all file nodes under a tree entry (recursive). */
function countFiles(entries: TreeEntry[]): number {
  let n = 0;
  for (const e of entries) {
    if (e.type === "file") n++;
    else n += countFiles(e.children);
  }
  return n;
}

/** Collect all file IDs under a folder (recursive). */
function collectFileIds(entries: TreeEntry[]): string[] {
  const ids: string[] = [];
  for (const e of entries) {
    if (e.type === "file") ids.push(e.item.fileId);
    else ids.push(...collectFileIds(e.children));
  }
  return ids;
}

/** Collect only non-included file IDs (for selection/checkbox). */
function collectSelectableFileIds(entries: TreeEntry[]): string[] {
  const ids: string[] = [];
  for (const e of entries) {
    if (e.type === "file" && !e.item.isIncluded) ids.push(e.item.fileId);
    else if (e.type === "folder") ids.push(...collectSelectableFileIds(e.children));
  }
  return ids;
}

// ── Search helpers ──

/** Check whether any descendant file name matches the query. */
function hasMatchingDescendant(entry: TreeEntry, query: string): boolean {
  if (entry.type === "file") {
    return entry.name.toLowerCase().includes(query);
  }
  return entry.children.some((child) => hasMatchingDescendant(child, query));
}

// ── Flatten tree into virtual rows ──

function flattenTree(
  entries: TreeEntry[],
  expandedPaths: Set<string>,
  depth: number,
  searchQuery: string,
): VirtualTreeRow[] {
  const rows: VirtualTreeRow[] = [];
  const query = searchQuery.toLowerCase();

  for (const entry of entries) {
    if (entry.type === "folder") {
      const folder = entry;

      // When searching, skip folders that have no matching descendants
      if (query && !hasMatchingDescendant(folder, query)) continue;

      const fileIds = collectFileIds(folder.children);
      const selectableFileIds = collectSelectableFileIds(folder.children);
      const fileCount = countFiles(folder.children);
      // When searching, force all folders expanded
      const isExpanded = query ? true : expandedPaths.has(folder.path);

      rows.push({
        type: "folder",
        depth,
        name: folder.name,
        path: folder.path,
        fileCount,
        fileIds,
        selectableFileIds,
        isExpanded,
      });

      if (isExpanded) {
        rows.push(...flattenTree(folder.children, expandedPaths, depth + 1, searchQuery));
      }
    } else {
      // File
      if (query && !entry.name.toLowerCase().includes(query)) continue;
      rows.push({ type: "file", depth, item: entry.item });
    }
  }

  return rows;
}

// ── Props ──

export interface ProcurementCatalogPaneProps {
  items: ProcurementFileItem[];
  highlightedFileIds?: string[];
  selectedIds: Set<string>;
  onSelect: (fileId: string, e: React.MouseEvent) => void;
  onSelectFolder: (ids: string[]) => void;
  // Drag-and-drop
  onDragStart: (
    pane: "catalog" | "included",
    fileId: string,
    e: React.DragEvent<HTMLDivElement>,
  ) => void;
  onDragEnd: () => void;
  onDragOver: (pane: "catalog" | "included", e: React.DragEvent) => void;
  onDragLeave: (pane: "catalog" | "included", e: React.DragEvent) => void;
  onDrop: (pane: "catalog" | "included", e: React.DragEvent) => void;
  isDragOver: boolean;
  // Actions
  onPreview: (fileId: string, source?: string | null) => void;
  onToggleIncluded: (fileId: string, shouldInclude: boolean) => void;
  isUpdatePending: boolean;
  searchQuery: string;
  // Feedback
  onFeedback?: (fileId: string, fb: "should_include" | "confirm" | "doublecheck") => void;
  feedbackByFile?: Record<string, "keep" | "remove" | "should_include" | "confirm" | "doublecheck">;
  feedbackCommentsByFile?: Record<string, string>;
  onCommentChange?: (fileId: string, comment: string) => void;
  onCommentSubmit?: (fileId: string) => void;
  // Title-block metadata
  selectedProperties?: string[];
  getTitleBlockProperty?: (tb: TitleBlockData | null | undefined, prop: string) => string | null;
  // Active file (for center preview)
  activePreviewId?: string;
  onActiveChange?: (fileId: string) => void;
  // Empty click
  onEmptyClick?: () => void;
}

// ── Memoized row renderers ──

interface FolderRowProps {
  row: Extract<VirtualTreeRow, { type: "folder" }>;
  selectedIds: Set<string>;
  onToggleExpand: (path: string) => void;
  onSelectFolder: (ids: string[]) => void;
}

const FolderRow = memo(function FolderRow({
  row,
  selectedIds,
  onToggleExpand,
  onSelectFolder,
}: FolderRowProps) {
  const { t } = useTranslation();
  // Only count selectable (non-included) files for checkbox state
  const selectable = row.selectableFileIds;
  const allSelected = selectable.length > 0 && selectable.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && selectable.some((id) => selectedIds.has(id));

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (allSelected) {
        // Deselect all selectable files in this folder
        const folderSet = new Set(selectable);
        onSelectFolder(Array.from(selectedIds).filter((id) => !folderSet.has(id)));
      } else {
        // Select all selectable files in this folder
        onSelectFolder([...new Set([...Array.from(selectedIds), ...selectable])]);
      }
    },
    [allSelected, selectable, selectedIds, onSelectFolder],
  );

  return (
    <div
      className="flex items-center py-1.5 px-2 rounded-md hover:bg-accent cursor-pointer transition-colors select-none"
      style={{ paddingLeft: `${row.depth * 20 + 8}px` }}
      onClick={() => onToggleExpand(row.path)}
    >
      {/* Folder checkbox */}
      <button
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border mr-1.5 transition-colors",
          allSelected
            ? "border-primary bg-primary text-primary-foreground"
            : someSelected
              ? "border-primary bg-primary/20"
              : "border-muted-foreground/40 hover:border-muted-foreground",
        )}
        onClick={handleCheckboxClick}
        aria-label={`${allSelected ? t("procurement.packageDetail.selection.clear") : t("procurement.packageDetail.selection.selectAll")} — ${row.name}`}
      >
        {allSelected && <Check className="h-3 w-3" />}
        {someSelected && <Minus className="h-3 w-3 text-primary" />}
      </button>

      {/* Expand chevron */}
      {row.isExpanded ? (
        <ChevronDown className="h-4 w-4 text-muted-foreground mr-1 shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 text-muted-foreground mr-1 shrink-0" />
      )}

      {/* Folder icon */}
      {row.isExpanded ? (
        <FolderOpen className="h-4 w-4 text-primary mr-2 shrink-0" />
      ) : (
        <Folder className="h-4 w-4 text-primary mr-2 shrink-0" />
      )}

      {/* Name + count */}
      <span className="text-sm font-semibold truncate flex-1 text-foreground/80">{row.name}</span>
      <span className="text-ui-body-xs text-muted-foreground ml-2 tabular-nums">
        {row.fileCount}
      </span>
    </div>
  );
});

interface FileRowProps {
  row: Extract<VirtualTreeRow, { type: "file" }>;
  isSelected: boolean;
  isActive: boolean;
  isHighlighted: boolean;
  searchQuery: string;
  selectedProperties?: string[];
  getTitleBlockProperty?: (tb: TitleBlockData | null | undefined, prop: string) => string | null;
  onSelect: (fileId: string, e: React.MouseEvent) => void;
  onActiveChange?: (fileId: string) => void;
  onDragStart: (
    pane: "catalog" | "included",
    fileId: string,
    e: React.DragEvent<HTMLDivElement>,
  ) => void;
  onDragEnd: () => void;
  onPreview: (fileId: string, source?: string | null) => void;
  onToggleIncluded: (fileId: string, shouldInclude: boolean) => void;
  isUpdatePending: boolean;
  // Feedback
  activeFeedback?: "keep" | "remove" | "should_include" | "confirm" | "doublecheck";
  feedbackComment?: string;
  onFeedback?: (fb: "should_include" | "confirm" | "doublecheck") => void;
  onCommentChange?: (comment: string) => void;
  onCommentSubmit?: () => void;
}

// ── Catalog feedback buttons (shared between FileRow instances) ──

interface CatalogFeedbackButtonsProps {
  activeFeedback?: "keep" | "remove" | "should_include" | "confirm" | "doublecheck";
  feedbackComment?: string;
  commentPopoverOpen: boolean;
  setCommentPopoverOpen: (open: boolean) => void;
  onFeedback: (fb: "should_include" | "confirm" | "doublecheck") => void;
  onCommentChange?: (comment: string) => void;
  onCommentSubmit?: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}

function CatalogFeedbackButtons({
  activeFeedback,
  feedbackComment,
  commentPopoverOpen,
  setCommentPopoverOpen,
  onFeedback,
  onCommentChange,
  onCommentSubmit,
  t,
}: CatalogFeedbackButtonsProps) {
  const handleClick = (fb: "should_include" | "confirm" | "doublecheck", e: React.MouseEvent) => {
    e.stopPropagation();
    const isToggleOff = activeFeedback === fb;
    onFeedback(fb);
    setCommentPopoverOpen(!isToggleOff);
  };

  const commentPopover = (label: string, placeholder: string) => (
    <PopoverContent
      className="w-72 p-3"
      side="bottom"
      align="start"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">{label}</p>
        <Textarea
          value={feedbackComment ?? ""}
          onChange={(e) => onCommentChange?.(e.target.value)}
          placeholder={placeholder}
          className="min-h-[80px] text-sm resize-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onCommentSubmit?.();
              setCommentPopoverOpen(false);
            }
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setCommentPopoverOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onCommentSubmit?.();
              setCommentPopoverOpen(false);
            }}
          >
            <Send className="size-4 mr-1" />
            {t("common.save")}
          </Button>
        </div>
        {feedbackComment && (
          <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
            <span className="font-medium">
              {t("procurement.packageDetail.feedback.commentLabel")}:
            </span>{" "}
            {feedbackComment}
          </div>
        )}
      </div>
    </PopoverContent>
  );

  return (
    <>
      <Popover
        open={commentPopoverOpen && activeFeedback === "should_include"}
        onOpenChange={(open) => {
          if (!open) setCommentPopoverOpen(false);
        }}
      >
        <PopoverTrigger asChild>
          <button
            className={cn(
              "h-4 w-4 inline-flex items-center justify-center rounded-sm transition-colors",
              activeFeedback === "should_include"
                ? "text-emerald-600"
                : "text-muted-foreground/40 hover:text-emerald-600",
            )}
            onClick={(e) => handleClick("should_include", e)}
            aria-label={t("procurement.packageDetail.feedback.include")}
          >
            <ThumbsUp className="h-2.5 w-2.5" />
          </button>
        </PopoverTrigger>
        {commentPopover(
          t("procurement.packageDetail.feedback.commentLabel"),
          t("procurement.packageDetail.feedback.commentPlaceholder"),
        )}
      </Popover>
      <Popover
        open={commentPopoverOpen && activeFeedback === "confirm"}
        onOpenChange={(open) => {
          if (!open) setCommentPopoverOpen(false);
        }}
      >
        <PopoverTrigger asChild>
          <button
            className={cn(
              "h-4 w-4 inline-flex items-center justify-center rounded-sm transition-colors",
              activeFeedback === "confirm"
                ? "text-destructive"
                : "text-muted-foreground/40 hover:text-destructive",
            )}
            onClick={(e) => handleClick("confirm", e)}
            aria-label={t("procurement.packageDetail.feedback.confirmExclude")}
          >
            <ThumbsDown className="h-2.5 w-2.5" />
          </button>
        </PopoverTrigger>
        {commentPopover(
          t("procurement.packageDetail.feedback.commentLabel"),
          t("procurement.packageDetail.feedback.commentPlaceholder"),
        )}
      </Popover>
      <Popover
        open={commentPopoverOpen && activeFeedback === "doublecheck"}
        onOpenChange={(open) => {
          if (!open) setCommentPopoverOpen(false);
        }}
      >
        <PopoverTrigger asChild>
          <button
            className={cn(
              "h-4 w-4 inline-flex items-center justify-center rounded-sm transition-colors",
              activeFeedback === "doublecheck"
                ? "text-amber-600"
                : "text-muted-foreground/40 hover:text-amber-600",
            )}
            onClick={(e) => handleClick("doublecheck", e)}
            aria-label={t("procurement.packageDetail.feedback.doublecheck")}
          >
            <HelpCircle className="h-2.5 w-2.5" />
          </button>
        </PopoverTrigger>
        {commentPopover(
          t("procurement.packageDetail.feedback.doublecheck"),
          t("procurement.packageDetail.feedback.doublecheckCommentPlaceholder"),
        )}
      </Popover>
    </>
  );
}

const FileRow = memo(function FileRow({
  row,
  isSelected,
  isActive,
  isHighlighted,
  searchQuery,
  selectedProperties,
  getTitleBlockProperty,
  onSelect,
  onActiveChange,
  onDragStart,
  onDragEnd,
  onPreview,
  onToggleIncluded,
  isUpdatePending,
  activeFeedback,
  feedbackComment,
  onFeedback,
  onCommentChange,
  onCommentSubmit,
}: FileRowProps) {
  const { t } = useTranslation();
  const [commentPopoverOpen, setCommentPopoverOpen] = useState(false);
  const file = row.item;
  const baseName = file.fileName.split("/").pop() || file.fileName;
  const isIncluded = file.isIncluded === true;
  const matchesSearch = searchQuery
    ? baseName.toLowerCase().includes(searchQuery.toLowerCase())
    : false;

  // Title-block property values
  const propertyElements =
    selectedProperties && getTitleBlockProperty && file.titleBlockData
      ? selectedProperties
          .map((prop) => {
            const value = getTitleBlockProperty(file.titleBlockData, prop);
            if (!value) return null;
            return (
              <span key={prop}>
                <span className="font-semibold">
                  {t(`procurement.packageDetail.titleBlock.${prop}`)}:
                </span>{" "}
                {value}
              </span>
            );
          })
          .filter(Boolean)
      : [];
  const hasProperties = propertyElements.length > 0;

  return (
    <div
      data-file-id={isIncluded ? undefined : file.fileId}
      draggable={!isIncluded}
      onDragStart={isIncluded ? undefined : (e) => onDragStart("catalog", file.fileId, e)}
      onDragEnd={isIncluded ? undefined : onDragEnd}
      className={cn(
        "flex items-start py-1 px-2 rounded-md select-none transition-colors border",
        isIncluded
          ? "opacity-40 cursor-default border-transparent"
          : cn(
              "cursor-default group",
              isActive
                ? "border-primary bg-primary/5"
                : isHighlighted
                  ? "border-info-border bg-info-muted/45"
                  : isSelected
                    ? "bg-accent border-border"
                    : "hover:bg-accent/50 border-transparent",
              searchQuery && matchesSearch && "bg-warning/10",
            ),
      )}
      style={{ paddingLeft: `${row.depth * 20 + 8}px` }}
      onClick={
        isIncluded
          ? undefined
          : (e) => {
              if ((e.target as HTMLElement).closest("button")) return;
              // Always update selection (sets anchor for shift-click ranges)
              onSelect(file.fileId, e);
              // Plain click also updates the preview
              if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
                onActiveChange?.(file.fileId);
              }
            }
      }
      onDoubleClick={isIncluded ? undefined : () => onPreview(file.fileId, file.source)}
    >
      {/* Spacer matching folder chevron width */}
      <span className="w-6 shrink-0 mt-0.5" />
      <span className="mr-2 shrink-0 mt-0.5">{getFileIcon(baseName)}</span>

      {/* Content area: name on line 1, properties on line 2 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span data-card-name className="text-sm truncate flex-1 font-medium">
            {baseName}
          </span>
          {isIncluded && (
            <span className="text-[10px] inline-flex items-center rounded-full bg-primary/10 text-primary px-1.5 py-1 leading-none shrink-0">
              {t("procurement.packageDetail.catalog.included")}
            </span>
          )}
        </div>

        {/* Title-block properties */}
        {hasProperties && (
          <div className="text-ui-body-xs line-clamp-2 flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground mt-0.5">
            {propertyElements}
          </div>
        )}
        {/* Feedback buttons + badges for non-included files */}
        {!isIncluded && onFeedback && (
          <div className="flex items-center gap-1 mt-0.5">
            <CatalogFeedbackButtons
              activeFeedback={activeFeedback}
              feedbackComment={feedbackComment}
              commentPopoverOpen={commentPopoverOpen}
              setCommentPopoverOpen={setCommentPopoverOpen}
              onFeedback={onFeedback}
              onCommentChange={onCommentChange}
              onCommentSubmit={onCommentSubmit}
              t={t}
            />
            {activeFeedback === "should_include" && (
              <span className="text-[10px] inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-px leading-none">
                {t("procurement.packageDetail.feedback.include")}
              </span>
            )}
            {activeFeedback === "confirm" && (
              <span className="text-[10px] inline-flex items-center rounded-full bg-destructive/15 text-destructive px-1.5 py-px leading-none">
                {t("procurement.packageDetail.feedback.confirmExclude")}
              </span>
            )}
            {activeFeedback === "doublecheck" && (
              <span className="text-[10px] inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-1.5 py-px leading-none">
                {t("procurement.packageDetail.feedback.doublecheck")}
              </span>
            )}
            {/* Feedback comment — inside !isIncluded so included catalog rows
                never show an orphaned comment without matching buttons/badges */}
            {feedbackComment && activeFeedback && (
              <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1 group-hover:line-clamp-3 italic">
                &ldquo;{feedbackComment}&rdquo;
              </p>
            )}
          </div>
        )}
      </div>

      {/* Action buttons (visible on hover) — hidden for included files */}
      {!isIncluded && (
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1 mt-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onPreview(file.fileId, file.source)}
                aria-label={t("procurement.packageDetail.actions.preview")}
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t("procurement.packageDetail.actions.preview")}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground"
                onClick={() => onToggleIncluded(file.fileId, true)}
                disabled={isUpdatePending}
                aria-label={t("procurement.packageDetail.actions.add")}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t("procurement.packageDetail.actions.add")}
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
});

// ── Main component ──

export const ProcurementCatalogPane: React.FC<ProcurementCatalogPaneProps> = ({
  items,
  highlightedFileIds = [],
  selectedIds,
  onSelect,
  onSelectFolder,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver,
  onPreview,
  onToggleIncluded,
  isUpdatePending,
  searchQuery,
  onFeedback,
  feedbackByFile,
  feedbackCommentsByFile,
  onCommentChange,
  onCommentSubmit,
  selectedProperties,
  getTitleBlockProperty,
  activePreviewId,
  onActiveChange,
  onEmptyClick,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Track expanded folders — default all expanded (not in the collapsed set)
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  // We use a separate flag for "has user ever interacted" to know whether
  // to auto-expand all or use explicit state
  const [hasInteracted, setHasInteracted] = useState(false);

  const tree = useMemo(() => buildTree(items), [items]);

  // Effective expanded set: all folders collapsed by default until user interacts
  const effectiveExpanded = useMemo(() => {
    if (!hasInteracted) return new Set<string>();
    return expandedPaths;
  }, [hasInteracted, expandedPaths]);

  const toggleFolder = useCallback((path: string) => {
    setHasInteracted(true);
    setExpandedPaths((prev) => {
      const base = new Set(prev);
      if (base.has(path)) {
        base.delete(path);
      } else {
        base.add(path);
      }
      return base;
    });
  }, []);

  // Flatten tree into virtual rows
  const flatList = useMemo(
    () => flattenTree(tree, effectiveExpanded, 0, searchQuery),
    [tree, effectiveExpanded, searchQuery],
  );

  // Virtualizer
  const virtualizer = useVirtualizer({
    count: flatList.length,
    getScrollElement: () => containerRef.current,
    estimateSize: (i) => {
      if (flatList[i].type === "folder") return 36;
      return selectedProperties?.length ? 52 : 32;
    },
    overscan: 20,
  });

  // Rubber-band (lasso) multi-select — skip included files
  const getItemLayout = useCallback(() => {
    const items: Array<{ id: string; top: number; height: number }> = [];
    let top = 0;
    const fileRowHeight = selectedProperties?.length ? 52 : 32;
    for (const row of flatList) {
      const height = row.type === "folder" ? 36 : fileRowHeight;
      if (row.type === "file" && !row.item.isIncluded) {
        items.push({ id: row.item.fileId, top, height });
      }
      top += height;
    }
    return items;
  }, [flatList, selectedProperties]);

  const rubberBand = useRubberBand({
    containerRef: containerRef as React.RefObject<HTMLElement>,
    itemSelector: "[data-file-id]",
    onSelectionChange: onSelectFolder,
    isEnabled: true,
    baseSelection: Array.from(selectedIds),
    getItemLayout,
  });

  // Container click handler (empty-space click clears selection)
  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      if (rubberBand.justFinishedRef.current) return;
      const target = e.target as HTMLElement;
      if (target.closest("[data-file-id]") || target.closest("button, a, input")) return;
      onEmptyClick?.();
    },
    [rubberBand.justFinishedRef, onEmptyClick],
  );

  return (
    <div
      className={cn(
        "relative flex-1 overflow-hidden min-h-0",
        isDragOver && "outline-2 outline-dashed outline-blue-500 -outline-offset-2 bg-blue-500/6",
      )}
      onDragOver={(e) => onDragOver("catalog", e)}
      onDragLeave={(e) => onDragLeave("catalog", e)}
      onDrop={(e) => onDrop("catalog", e)}
    >
      <div
        ref={containerRef}
        className="h-full overflow-y-auto pb-10"
        onClick={handleContainerClick}
        onPointerDown={rubberBand.handlePointerDown}
      >
        {flatList.length === 0 && (
          <p className="px-4 py-6 text-sm italic text-muted-foreground">
            {searchQuery
              ? t("procurement.packageDetail.catalog.noResults")
              : t("procurement.packageDetail.catalog.empty")}
          </p>
        )}
        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: "relative",
            overflow: "hidden",
          }}
        >
          {virtualizer.getVirtualItems().map((vItem) => {
            const row = flatList[vItem.index];
            return (
              <div
                key={vItem.key}
                ref={virtualizer.measureElement}
                data-index={vItem.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${vItem.start}px)`,
                }}
              >
                {row.type === "folder" ? (
                  <FolderRow
                    row={row}
                    selectedIds={selectedIds}
                    onToggleExpand={toggleFolder}
                    onSelectFolder={onSelectFolder}
                  />
                ) : (
                  <FileRow
                    row={row}
                    isSelected={selectedIds.has(row.item.fileId)}
                    isActive={row.item.fileId === activePreviewId}
                    isHighlighted={highlightedFileIds.includes(row.item.fileId)}
                    searchQuery={searchQuery}
                    selectedProperties={selectedProperties}
                    getTitleBlockProperty={getTitleBlockProperty}
                    onSelect={onSelect}
                    onActiveChange={onActiveChange}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onPreview={onPreview}
                    onToggleIncluded={onToggleIncluded}
                    isUpdatePending={isUpdatePending}
                    activeFeedback={feedbackByFile?.[row.item.fileId]}
                    feedbackComment={feedbackCommentsByFile?.[row.item.fileId]}
                    onFeedback={onFeedback ? (fb) => onFeedback(row.item.fileId, fb) : undefined}
                    onCommentChange={
                      onCommentChange
                        ? (comment) => onCommentChange(row.item.fileId, comment)
                        : undefined
                    }
                    onCommentSubmit={
                      onCommentSubmit ? () => onCommentSubmit(row.item.fileId) : undefined
                    }
                  />
                )}
              </div>
            );
          })}
          <div
            ref={rubberBand.rubberBandRef}
            className="border border-blue-500/60 bg-blue-500/10 rounded-sm pointer-events-none"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 50,
              display: "none",
            }}
          />
        </div>
      </div>
    </div>
  );
};
